/* eslint-disable no-unused-vars */
import React, { useEffect, useState, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { authActions } from '../../core/action/authActions';
import { companyActions } from '../../core/action/companyActions';
import FeatherIcon from "feather-icons-react";
import ImageWithBasePath from "../../core/img/imagewithbasebath";
import all_routes from "../../Router/all_routes";
import '../../style/css/header.css';

const Header = () => {
  const route = all_routes;
  const navigate = useNavigate();
  const [toggle, SetToggle] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [user, setUser] = useState(null);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [companySettings, setCompanySettings] = useState(null);
  const dropdownRef = useRef(null);

  // Load user data
  useEffect(() => {
    const loadUser = () => {
      const currentUser = authActions.getCurrentUser();
      console.log('Current user from storage:', currentUser);
      setUser(currentUser);
    };
    
    loadUser();
    
    // Listen for storage events (for multi-tab sync)
    const handleStorageChange = () => {
      const currentUser = authActions.getCurrentUser();
      console.log('Storage changed, user:', currentUser);
      setUser(currentUser);
    };
    
    window.addEventListener('storage', handleStorageChange);
    
    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []);

  // Load company settings (logo/favicon)
  useEffect(() => {
    const loadCompany = async () => {
      try {
        const result = await companyActions.getSettings();
        if (result.success) {
          const formatted = companyActions.formatCompanyData(result.data || {});
          setCompanySettings(formatted);

          // Update document favicon if available
          if (formatted.full_favicon_path) {
            let link = document.querySelector("link[rel~='icon']");
            if (!link) {
              link = document.createElement('link');
              link.rel = 'icon';
              document.getElementsByTagName('head')[0].appendChild(link);
            }
            link.href = formatted.full_favicon_path;
          }
        }
      } catch (err) {
        console.error('Failed to load company settings', err);
      }
    };

    loadCompany();
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDropdownOpen(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Get user initials for avatar fallback
  const getUserInitials = () => {
    if (!user?.name) return 'U';
    return user.name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  // Get user role display name
  const getUserRole = () => {
    if (!user?.role_name) return 'User';
    return user.role_name;
  };

  // Check if user is admin
  const isAdmin = () => {
    if (!user) return false;
    return user.role_slug === 'super-admin' || user.role_slug === 'admin';
  };

  const isElementVisible = (element) => {
    return element.offsetWidth > 0 || element.offsetHeight > 0;
  };

  const slideDownSubmenu = () => {
    const subdropPlusUl = document.getElementsByClassName("subdrop");
    for (let i = 0; i < subdropPlusUl.length; i++) {
      const submenu = subdropPlusUl[i].nextElementSibling;
      if (submenu && submenu.tagName.toLowerCase() === "ul") {
        submenu.style.display = "block";
      }
    }
  };

  const slideUpSubmenu = () => {
    const subdropPlusUl = document.getElementsByClassName("subdrop");
    for (let i = 0; i < subdropPlusUl.length; i++) {
      const submenu = subdropPlusUl[i].nextElementSibling;
      if (submenu && submenu.tagName.toLowerCase() === "ul") {
        submenu.style.display = "none";
      }
    }
  };

  useEffect(() => {
    const handleMouseover = (e) => {
      e.stopPropagation();

      const body = document.body;
      const toggleBtn = document.getElementById("toggle_btn");

      if (
        body.classList.contains("mini-sidebar") &&
        isElementVisible(toggleBtn)
      ) {
        const target = e.target.closest(".sidebar, .header-left");

        if (target) {
          body.classList.add("expand-menu");
          slideDownSubmenu();
        } else {
          body.classList.remove("expand-menu");
          slideUpSubmenu();
        }

        e.preventDefault();
      }
    };

    document.addEventListener("mouseover", handleMouseover);

    return () => {
      document.removeEventListener("mouseover", handleMouseover);
    };
  }, []);

  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(
        document.fullscreenElement ||
          document.mozFullScreenElement ||
          document.webkitFullscreenElement ||
          document.msFullscreenElement
      );
    };

    document.addEventListener("fullscreenchange", handleFullscreenChange);
    document.addEventListener("mozfullscreenchange", handleFullscreenChange);
    document.addEventListener("webkitfullscreenchange", handleFullscreenChange);
    document.addEventListener("msfullscreenchange", handleFullscreenChange);

    return () => {
      document.removeEventListener("fullscreenchange", handleFullscreenChange);
      document.removeEventListener(
        "mozfullscreenchange",
        handleFullscreenChange
      );
      document.removeEventListener(
        "webkitfullscreenchange",
        handleFullscreenChange
      );
      document.removeEventListener(
        "msfullscreenchange",
        handleFullscreenChange
      );
    };
  }, []);

  const handlesidebar = () => {
    document.body.classList.toggle("mini-sidebar");
    SetToggle((current) => !current);
  };

  const expandMenu = () => {
    document.body.classList.remove("expand-menu");
  };

  const expandMenuOpen = () => {
    document.body.classList.add("expand-menu");
  };

  const sidebarOverlay = () => {
    document?.querySelector(".main-wrapper")?.classList?.toggle("slide-nav");
    document?.querySelector(".sidebar-overlay")?.classList?.toggle("opened");
    document?.querySelector("html")?.classList?.toggle("menu-opened");
  };

  const toggleFullscreen = (elem) => {
    elem = elem || document.documentElement;
    if (
      !document.fullscreenElement &&
      !document.mozFullScreenElement &&
      !document.webkitFullscreenElement &&
      !document.msFullscreenElement
    ) {
      if (elem.requestFullscreen) {
        elem.requestFullscreen();
      } else if (elem.msRequestFullscreen) {
        elem.msRequestFullscreen();
      } else if (elem.mozRequestFullScreen) {
        elem.mozRequestFullScreen();
      } else if (elem.webkitRequestFullscreen) {
        elem.webkitRequestFullscreen(Element.ALLOW_KEYBOARD_INPUT);
      }
    } else {
      if (document.exitFullscreen) {
        document.exitFullscreen();
      } else if (document.msExitFullscreen) {
        document.msExitFullscreen();
      } else if (document.mozCancelFullScreen) {
        document.mozCancelFullScreen();
      } else if (document.webkitExitFullscreen) {
        document.webkitExitFullscreen();
      }
    }
  };

  // Toggle dropdown
  const toggleDropdown = (e) => {
    e.preventDefault();
    setDropdownOpen(!dropdownOpen);
  };

  // Handle logout
  const handleLogout = (e) => {
    e.preventDefault();
    authActions.logout();
    navigate(route.signin);
    setDropdownOpen(false);
  };

  let pathname = location.pathname;

  const exclusionArray = [
    "/reactjs/template/dream-pos/index-three",
    "/reactjs/template/dream-pos/index-one",
  ];
  
  if (exclusionArray.indexOf(window.location.pathname) >= 0) {
    return "";
  }

  return (
    <>
      <div className="header">
        {/* Logo */}
        <div
          className={`header-left ${toggle ? "" : "active"}`}
          onMouseLeave={expandMenu}
          onMouseOver={expandMenuOpen}
        >
          <Link to={route.dashboard} className="logo logo-normal">
            <ImageWithBasePath src={companySettings?.full_logo_path || 'assets/img/logo.png'} alt="img" />
          </Link>
          <Link to={route.dashboard} className="logo logo-white">
            <ImageWithBasePath src={companySettings?.full_logo_path || 'assets/img/logo-white.png'} alt="img" />
          </Link>
          <Link to={route.dashboard} className="logo-small">
            <ImageWithBasePath src={companySettings?.full_favicon_path || companySettings?.full_logo_path || 'assets/img/logo-small.png'} alt="img" />
          </Link>
          <Link
            id="toggle_btn"
            to="#"
            style={{
              display: pathname.includes("tasks")
                ? "none"
                : pathname.includes("compose")
                ? "none"
                : "",
            }}
            onClick={handlesidebar}
          >
            <FeatherIcon icon="chevrons-left" className="feather-16" />
          </Link>
        </div>
        {/* /Logo */}
        
        <Link
          id="mobile_btn"
          className="mobile_btn"
          to="#"
          onClick={sidebarOverlay}
        >
          <span className="bar-icon">
            <span />
            <span />
            <span />
          </span>
        </Link>
        
        {/* Header Menu */}
        <ul className="nav user-menu">
          <li className="nav-item nav-item-box">
            <Link
              to="#"
              id="btnFullscreen"
              onClick={() => toggleFullscreen()}
              className={isFullscreen ? "Exit Fullscreen" : "Go Fullscreen"}
            >
              <FeatherIcon icon="maximize" />
            </Link>
          </li>
          
          {/* <li className="nav-item nav-item-box">
            <Link to={route.email}>
              <FeatherIcon icon="mail" />
              <span className="badge rounded-pill">1</span>
            </Link>
          </li> */}
          
          {/* Notifications */}
          {/* <li className="nav-item dropdown nav-item-box">
            <Link
              to="#"
              className="dropdown-toggle nav-link"
              data-bs-toggle="dropdown"
            >
              <FeatherIcon icon="bell" />
              <span className="badge rounded-pill">2</span>
            </Link>
            <div className="dropdown-menu notifications">
              <div className="topnav-dropdown-header">
                <span className="notification-title">Notifications</span>
                <Link to="#" className="clear-noti">
                  Clear All
                </Link>
              </div>
              <div className="noti-content">
                <ul className="notification-list">
                  <li className="notification-message active">
                    <Link to="/activities">
                      <div className="media d-flex">
                        <span className="avatar flex-shrink-0">
                          <ImageWithBasePath
                            alt="img"
                            src="assets/img/profiles/avatar-02.jpg"
                          />
                        </span>
                        <div className="media-body flex-grow-1">
                          <p className="noti-details">
                            <span className="noti-title">John Doe</span> added
                            new task{" "}
                            <span className="noti-title">
                              Patient appointment booking
                            </span>
                          </p>
                          <p className="noti-time">
                            <span className="notification-time">
                              4 mins ago
                            </span>
                          </p>
                        </div>
                      </div>
                    </Link>
                  </li>
                  <li className="notification-message">
                    <Link to="/activities">
                      <div className="media d-flex">
                        <span className="avatar flex-shrink-0">
                          <ImageWithBasePath
                            alt="img"
                            src="assets/img/profiles/avatar-03.jpg"
                          />
                        </span>
                        <div className="media-body flex-grow-1">
                          <p className="noti-details">
                            <span className="noti-title">Tarah Shropshire</span>{" "}
                            changed the task name{" "}
                            <span className="noti-title">
                              Appointment booking with payment gateway
                            </span>
                          </p>
                          <p className="noti-time">
                            <span className="notification-time">
                              6 mins ago
                            </span>
                          </p>
                        </div>
                      </div>
                    </Link>
                  </li>
                  <li className="notification-message">
                    <Link to="/activities">
                      <div className="media d-flex">
                        <span className="avatar flex-shrink-0">
                          <ImageWithBasePath
                            alt="img"
                            src="assets/img/profiles/avatar-06.jpg"
                          />
                        </span>
                        <div className="media-body flex-grow-1">
                          <p className="noti-details">
                            <span className="noti-title">Misty Tison</span>{" "}
                            added{" "}
                            <span className="noti-title">Domenic Houston</span>{" "}
                            and <span className="noti-title">Claire Mapes</span>{" "}
                            to project{" "}
                            <span className="noti-title">
                              Doctor available module
                            </span>
                          </p>
                          <p className="noti-time">
                            <span className="notification-time">
                              8 mins ago
                            </span>
                          </p>
                        </div>
                      </div>
                    </Link>
                  </li>
                </ul>
              </div>
              <div className="topnav-dropdown-footer">
                <Link to="/activities">View all Notifications</Link>
              </div>
            </div>
          </li> */}
          
          {/* /Notifications */}
          
          {/* <li className="nav-item nav-item-box">
            <Link to={route.generalsettings}>
              <FeatherIcon icon="settings" />
            </Link>
          </li> */}
          
          {/* User Menu - With React-controlled dropdown */}
          <li className="nav-item dropdown has-arrow main-drop" ref={dropdownRef}>
            <Link
              to="#"
              className="dropdown-toggle nav-link userset"
              onClick={toggleDropdown}
            >
              <span className="user-info">
                <span className="user-letter">
                  {user?.profile_image ? (
                    <ImageWithBasePath
                      src={user.profile_image}
                      alt={user.name}
                      className="img-fluid rounded-circle"
                    />
                  ) : (
                    <div className="avatar-placeholder bg-primary text-white rounded-circle d-flex align-items-center justify-content-center" 
                         style={{ width: '32px', height: '32px', fontSize: '14px', fontWeight: '600' }}>
                      {getUserInitials()}
                    </div>
                  )}
                </span>
                <span className="user-detail">
                  <span className="user-name">{user?.name || 'Guest'}</span>
                  <span className="user-role">{getUserRole()}</span>
                </span>
              </span>
            </Link>
            
            {dropdownOpen && (
              <div className="dropdown-menu show" style={{ 
                position: 'absolute', 
                top: '100%', 
                right: '0',
                left: 'auto',
                margin: '0',
                transform: 'translate3d(0px, 38.4px, 0px)',
                display: 'block',
                zIndex: 1000
              }}>
                <div className="profilename">
                  <div className="profileset">
                    <span className="user-img">
                      {user?.profile_image ? (
                        <ImageWithBasePath
                          src={user.profile_image}
                          alt={user.name}
                        />
                      ) : (
                        <div className="avatar-placeholder bg-primary text-white rounded-circle d-flex align-items-center justify-content-center"
                             style={{ width: '40px', height: '40px', fontSize: '16px', fontWeight: '600' }}>
                          {getUserInitials()}
                        </div>
                      )}
                      <span className="status online" />
                    </span>
                    <div className="profilesets">
                      <h6>{user?.name || 'Guest'}</h6>
                      <h5>{getUserRole()}</h5>
                    </div>
                  </div>
                  <hr className="m-0" />
                  
                  <Link className="dropdown-item" to="/profile" onClick={() => setDropdownOpen(false)}>
                    <i className="me-2" data-feather="user" /> My Profile
                  </Link>
                  
                  {isAdmin() && (
                    <Link className="dropdown-item" to="/company-settings" onClick={() => setDropdownOpen(false)}>
                      <i className="me-2" data-feather="settings" />
                      Company Settings
                    </Link>
                  )}
                  
                  <hr className="m-0" />
                  <Link 
                    className="dropdown-item logout pb-0" 
                    to="#"
                    onClick={handleLogout}
                  >
                    <ImageWithBasePath
                      src="assets/img/icons/log-out.svg"
                      alt="img"
                      className="me-2"
                    />
                    Logout
                  </Link>
                </div>
              </div>
            )}
          </li>
        </ul>
        
        {/* Mobile Menu */}
        <div className="dropdown mobile-user-menu">
          <Link
            to="#"
            className="nav-link dropdown-toggle"
            data-bs-toggle="dropdown"
            aria-expanded="false"
          >
            <i className="fa fa-ellipsis-v" />
          </Link>
          <div className="dropdown-menu dropdown-menu-right">
            <Link className="dropdown-item" to={route.profile}>
              My Profile
            </Link>
            <Link className="dropdown-item" to={route.generalsettings}>
              Settings
            </Link>
            <Link 
              className="dropdown-item" 
              to="#"
              onClick={handleLogout}
            >
              Logout
            </Link>
          </div>
        </div>
        {/* /Mobile Menu */}
      </div>

      
    </>
  );
};

export default Header;